/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function formatMoneyGlobal(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/,
    formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."),
      dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        "$1" + thousands
      ),
      centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

const documentAccordions = document.querySelectorAll(".accordion");
if (documentAccordions) {
  documentAccordions.forEach(function(el) {
    let triggers = el.querySelectorAll(".accordionHeader");
    triggers.forEach(function(trgr) {
      trgr.addEventListener("click", function(e) {
        let parentEl = trgr.parentElement;
        let siblingItems = el.querySelectorAll(".accordionItem");
        if (!parentEl.classList.contains("active")) {
          parentEl.classList.add("active");
          if (siblingItems) {
            siblingItems.forEach(function(sblng) {
              if (sblng !== parentEl) {
                sblng.classList.remove("active");
              }
            });
          }
        } else {
          parentEl.classList.remove("active");
        }
      });
    });
  });
}

var prodStatusAvailable = document.querySelector(".prodStatusAvailable");
var prodStatusSoldout = document.querySelector(".prodStatusSoldout");

document.addEventListener("variant:changed", function(event) {
  let variant = event.detail.variant;
  if (prodStatusAvailable && prodStatusSoldout) {
    if (typeof variant !== "undefined" && variant.available) {
      prodStatusAvailable.classList.remove("d-none");
      prodStatusSoldout.classList.add("d-none");
    } else {
      prodStatusAvailable.classList.add("d-none");
      prodStatusSoldout.classList.remove("d-none");
    }
  }
});

const toggleButtonLink = document.querySelectorAll(".toggleButtonLink");
if (toggleButtonLink) {
  toggleButtonLink.forEach(function(link) {
    link.addEventListener("click", function(e) {
      e.stopPropagation();
    });
  });
}

/* Cart Timer */
$(document).ready(function() {
  function startTimer() {
    var seconds, minutes;
    if (getCookie("checkouttimer")) {
      seconds = getCookie("checkouttimer");
      if (seconds == "NaN") {
        seconds = 600;
      }
      $(".js-mini-cart-timer__time").attr("data-seconds", seconds);
      minutes =
        addLeadingZeros(parseInt(seconds / 60), 2) +
        ":" +
        addLeadingZeros(parseInt(seconds % 60), 2);
      $(".js-mini-cart-timer__time").text(minutes);
    } else {
      seconds = 600;
    }
    var timer = setInterval(function() {
      seconds = $(".js-mini-cart-timer__time").attr("data-seconds");
      seconds--;
      if (seconds <= 0) {
        seconds = 600;
      }
      $(".js-mini-cart-timer__time").attr("data-seconds", seconds);
      minutes =
        addLeadingZeros(parseInt(seconds / 60), 2) +
        ":" +
        addLeadingZeros(parseInt(seconds % 60), 2);
      $(".js-mini-cart-timer__time").text(minutes);
      setCookie("checkouttimer", seconds, 1);
    }, 1000);
  }

  /*
  document.addEventListener('variant:added', function(event) {
    $('.js-mini-cart-timer__time').attr('data-seconds', 600);
    setCookie('checkouttimer', '600', 1);
  });
  */

  startTimer();

  $(document).on("click", ".customATCBtn", function() {
    let $form = $(this).parents(".customATCForm");
    let prodId = $form.data("product-id");
    let varId = $form.data("variant-id");
    if (prodId !== null && varId !== null) {
      let serializedData =
        "form_type=product&quantity=1&utf8=%E2%9C%93&data-product-id=" +
        prodId +
        "&id=" +
        varId;
      fetch("/cart/add.js", {
        method: "POST",
        body: serializedData,
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Requested-With": "XMLHttpRequest",
        },
      })
        .then((response) => response.json())
        .then(function(data) {
          if (data.status === 422) {
            this.error(data);
          } else {
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:refresh", {
                bubbles: true,
              })
            );
          }
        });
    }
  });

  document.body.addEventListener("docapp-discount-applied", (e) => {
    var cartDrawerGifts = document.querySelectorAll(".cart-drawer--gift");
    const summaryBlock = document.querySelector('.docapp-cart-with-coupon-summary--summary');
    if(summaryBlock){
      if(e.discounts.length){
        summaryBlock.classList.add('docapp-cart-with-coupon-summary--summary-with-discounts')
      } else {
        summaryBlock.classList.remove('docapp-cart-with-coupon-summary--summary-with-discounts')
      }
    }
    if (e.discounts && cartDrawerGifts.length > 0) {
      let hasScript = false;
      let hasDiscount = false;
      let upsellThreshold1 = cartDrawerGifts[0].dataset.threshold;
      let upsellThreshold2 = cartDrawerGifts[1].dataset.threshold;
      let upsellVariantId1 = cartDrawerGifts[0].dataset.variantId;
      let upsellVariantId2 = cartDrawerGifts[1].dataset.variantId;
      let cartTotal = e.cart.total_price;
      console.log(e);
      e.discounts.forEach(function(item) {
        if (item.type == "script") {
          hasScript = true;
        }
        if (item.type == "manual") {
          hasDiscount = true;
        }
      });
      if (hasDiscount) {
        if (hasScript) {
          if (cartTotal < upsellThreshold1) {
            if (
              document.querySelector(
                `.line-item--script[data-id="${upsellVariantId1}"] .line-item__remove-button`
              ) != null
            ) {
              document
                .querySelector(
                  `.line-item--script[data-id="${upsellVariantId1}"] .line-item__remove-button`
                )
                .click();
            }
          }
          if (cartTotal < upsellThreshold2) {
          }
        } else {
          if (cartTotal < upsellThreshold1) {
            document
              .querySelectorAll(
                ".cart-drawer--upsells--desktop .cart-drawer--gift"
              )[0]
              .classList.add("is-disabled");
            document
              .querySelectorAll(
                ".cart-drawer--upsells--desktop .cart-drawer--gift"
              )[0]
              .querySelector(".customATCBtn").innerHTML =
              '<svg xmlns="http://www.w3.org/2000/svg" width="23" height="25" viewBox="0 0 23 25" fill="none"><path d="M11.5502 0.38916C7.81139 0.38916 4.75997 3.44057 4.75997 7.17942V9.1195H2.8199C1.74801 9.1195 0.879822 9.98768 0.879822 11.0596V22.7C0.879822 23.7719 1.74801 24.6401 2.8199 24.6401H20.2806C21.3525 24.6401 22.2206 23.7719 22.2206 22.7V11.0596C22.2206 9.98768 21.3525 9.1195 20.2806 9.1195H18.3405V7.17942C18.3405 3.56272 15.4659 0.649679 11.8951 0.459261C11.7853 0.41507 11.6685 0.391313 11.5502 0.38916ZM11.5502 2.32924C14.2408 2.32924 16.4004 4.48886 16.4004 7.17942V9.1195H6.70005V7.17942C6.70005 4.48886 8.85967 2.32924 11.5502 2.32924Z" fill="white"/></svg>';
            document
              .querySelectorAll(
                ".cart-drawer--upsells--mobile .cart-drawer--gift"
              )[0]
              .classList.add("is-disabled");
            document
              .querySelectorAll(
                ".cart-drawer--upsells--mobile .cart-drawer--gift"
              )[0]
              .querySelector(".customATCBtn").innerHTML =
              '<svg xmlns="http://www.w3.org/2000/svg" width="23" height="25" viewBox="0 0 23 25" fill="none"><path d="M11.5502 0.38916C7.81139 0.38916 4.75997 3.44057 4.75997 7.17942V9.1195H2.8199C1.74801 9.1195 0.879822 9.98768 0.879822 11.0596V22.7C0.879822 23.7719 1.74801 24.6401 2.8199 24.6401H20.2806C21.3525 24.6401 22.2206 23.7719 22.2206 22.7V11.0596C22.2206 9.98768 21.3525 9.1195 20.2806 9.1195H18.3405V7.17942C18.3405 3.56272 15.4659 0.649679 11.8951 0.459261C11.7853 0.41507 11.6685 0.391313 11.5502 0.38916ZM11.5502 2.32924C14.2408 2.32924 16.4004 4.48886 16.4004 7.17942V9.1195H6.70005V7.17942C6.70005 4.48886 8.85967 2.32924 11.5502 2.32924Z" fill="white"/></svg>';
          }
          if (cartTotal < upsellThreshold2) {
            document
              .querySelectorAll(
                ".cart-drawer--upsells--desktop .cart-drawer--gift"
              )[1]
              .classList.add("is-disabled");
            document
              .querySelectorAll(
                ".cart-drawer--upsells--desktop .cart-drawer--gift"
              )[1]
              .querySelector(".customATCBtn").innerHTML =
              '<svg xmlns="http://www.w3.org/2000/svg" width="23" height="25" viewBox="0 0 23 25" fill="none"><path d="M11.5502 0.38916C7.81139 0.38916 4.75997 3.44057 4.75997 7.17942V9.1195H2.8199C1.74801 9.1195 0.879822 9.98768 0.879822 11.0596V22.7C0.879822 23.7719 1.74801 24.6401 2.8199 24.6401H20.2806C21.3525 24.6401 22.2206 23.7719 22.2206 22.7V11.0596C22.2206 9.98768 21.3525 9.1195 20.2806 9.1195H18.3405V7.17942C18.3405 3.56272 15.4659 0.649679 11.8951 0.459261C11.7853 0.41507 11.6685 0.391313 11.5502 0.38916ZM11.5502 2.32924C14.2408 2.32924 16.4004 4.48886 16.4004 7.17942V9.1195H6.70005V7.17942C6.70005 4.48886 8.85967 2.32924 11.5502 2.32924Z" fill="white"/></svg>';
            document
              .querySelectorAll(
                ".cart-drawer--upsells--mobile .cart-drawer--gift"
              )[1]
              .classList.add("is-disabled");
            document
              .querySelectorAll(
                ".cart-drawer--upsells--mobile .cart-drawer--gift"
              )[1]
              .querySelector(".customATCBtn").innerHTML =
              '<svg xmlns="http://www.w3.org/2000/svg" width="23" height="25" viewBox="0 0 23 25" fill="none"><path d="M11.5502 0.38916C7.81139 0.38916 4.75997 3.44057 4.75997 7.17942V9.1195H2.8199C1.74801 9.1195 0.879822 9.98768 0.879822 11.0596V22.7C0.879822 23.7719 1.74801 24.6401 2.8199 24.6401H20.2806C21.3525 24.6401 22.2206 23.7719 22.2206 22.7V11.0596C22.2206 9.98768 21.3525 9.1195 20.2806 9.1195H18.3405V7.17942C18.3405 3.56272 15.4659 0.649679 11.8951 0.459261C11.7853 0.41507 11.6685 0.391313 11.5502 0.38916ZM11.5502 2.32924C14.2408 2.32924 16.4004 4.48886 16.4004 7.17942V9.1195H6.70005V7.17942C6.70005 4.48886 8.85967 2.32924 11.5502 2.32924Z" fill="white"/></svg>';
          }
        }
      }
    }
  });

if (
    document.cookie.indexOf("newTemplateCookieViewV2=wischaufsatz") != -1 &&
document.querySelector(
"#shopify-section-template--17914491535624__ba422974-919a-44fd-b554-1d15196a185e"
    ) &&
document.querySelector(
"#shopify-section-template--17914491535624__7151818b-d6e4-4173-a509-5ddb3eefc670"
    )
  ) {
$(
"#shopify-section-template--17914491535624__ba422974-919a-44fd-b554-1d15196a185e"
    ).insertAfter(".shopify-section--main-product");
$(
"#shopify-section-template--17914491535624__7151818b-d6e4-4173-a509-5ddb3eefc670"
    ).insertAfter(
"#shopify-section-template--17914491535624__e69dd3cd-f850-490b-93f6-0b6589591bb9"
    );
  }
});

document.addEventListener("DOMContentLoaded", () => {
  const DISCOUNT_CODE = getCookie("discount_code");
  const HIDING_UPSELL_CODES = window.themeVariables.settings.hideUpsellCodes;
  const pdpUpsellBundles = document.querySelectorAll('.pdpUpsellBundles');
  if (DISCOUNT_CODE && HIDING_UPSELL_CODES && pdpUpsellBundles) {
    let upsellCodesArray = HIDING_UPSELL_CODES.split(',');
    if (upsellCodesArray && upsellCodesArray.includes(DISCOUNT_CODE)) {
      pdpUpsellBundles.forEach(el => {
        el.classList.add('d-none');
      });
    }
  }
});

// To delete (or better comment) bottom lines if this experiment goes live: https://app.ablyft.com/experiments/71248394
// document.addEventListener("DOMContentLoaded", () => {
//   var mediaToShow = document.querySelectorAll('[data-image-style="1"]');
//   var mediaToHide = document.querySelectorAll('[data-image-style="2"], [data-image-style="3"]'); 
//   mediaToShow && mediaToShow.forEach((media) => {
//     media.classList.remove('is-filtered');
//   });
//   if (mediaToHide) {
//     mediaToHide.forEach((media) => {
//       media.classList.add('is-filtered');
//     });
//     document.dispatchEvent(new CustomEvent('gallery-images:style-changed'));
//   }
// });
// To delete (or better comment) top lines if this experiment goes live: https://app.ablyft.com/experiments/71248394

(() => {
  function waitForElem(waitFor, callback, minElements = 1, isVariable = false, timer = 12e7, frequency = 250) {
    let elements = isVariable ? window[waitFor] : document.querySelectorAll(waitFor);
    if (timer <= 0)
      return;
    !isVariable && elements.length >= minElements || isVariable && typeof window[waitFor] !== "undefined" ? callback(elements) : setTimeout(
      () => waitForElem(
        waitFor,
        callback,
        minElements,
        isVariable,
        timer - frequency
      ),
      frequency
    );
  }
  var observer = new MutationObserver(() => {
    console.debug("mutation check");
    upsellUpdate();
  });
  observer.observe(document.querySelector("cart-drawer"), { childList: true, attributes: true });
  function upsellUpdate() {
    waitForElem("cart-drawer[open] .mini-cart__recommendations-heading.heading--xsmall", () => {
      document.querySelector(".mini-cart__recommendations-heading.heading--xsmall").innerHTML = window.cartUpsell.title;
    });
  }
  upsellUpdate();
})();


var PDPUpsellBundle = class extends HTMLElement {
  connectedCallback() {
    this.masterSelector = document.getElementById(this.getAttribute("form-id")).id;
    this.form = document.getElementById(this.getAttribute("form-id"));
    this.blocks = Array.from(this.querySelectorAll('[data-variant-upsell]'));
    this.updateBlocks();
    document.addEventListener('variant:changed', data => {
      this.updateBlocks();
    })

    const blockInputs = Array.from(this.querySelectorAll('[name="product-bundle-upsell"]'));
    const variantInputs = Array.from(this.querySelectorAll('.upsell-checkbox--variants input[data-with-toast]'));

    variantInputs.forEach(input => {
      input.addEventListener('change', e => {
        const variantsBlock = e.target.closest('.upsell-checkbox--variants');
        const productBlock = e.target.closest('.pdp-bundle-upsell');
        const productBlockInput = productBlock.querySelector('[name="product-bundle-upsell"]');

        const checkedVariants = Array.from(variantsBlock.querySelectorAll('input[data-with-toast]'));
        if(productBlockInput) productBlockInput.checked = true
      })
    })

    blockInputs.forEach(input => {
      input.addEventListener('change', e => {
        const parrent = e.target.closest('.pdp-bundle-upsell');
        const variantsBlock = parrent.querySelector('.upsell-checkbox--variants');
        const checkedVariants = Array.from(variantsBlock.querySelectorAll('input[data-with-toast]'));
        if(e.target.checked){
          checkedVariants[0].checked = true;
        } else {
          checkedVariants.forEach(i => i.checked = false);
        }
      })
    })
  }

  updateBlocks(){
    this.blocks.forEach(block => {
      block.style.display = 'none';
      const upsellCheckboxes = block.querySelectorAll('input[name="product-bundle-upsell"], input[type="radio"]:not([data-checked])');
      if(upsellCheckboxes.length){
        upsellCheckboxes.forEach(checkbox => checkbox.checked = false)
      }
    });
    const currentBlock = this.blocks.find(block => block.dataset.variantId == this.masterSelector.value);
    if(currentBlock) currentBlock.style.display = 'block';
  }


}

window.customElements.define("upsell-bundle", PDPUpsellBundle);